import { AbstractControl, Validators } from '@angular/forms';

/**
 * Remove caracteres repetidos da string.
 *
 * Normally use to define if the entire string is just some repeated number.
 *
 * @param {string} document Clean string with only the document numbers.
 *
 * @return {string} The document string without repeated values.
 */
function removeRepeatedChars(document: string): string {
	const matchFirstChar = new RegExp(`[${document.charAt(0)}]`, 'g');

	return document.replace(matchFirstChar, '');
}

/**
 * Performs the math behind document validation.
 *
 * This function finds the check digit of the document, from a base string.
 * The number is defined by the sum of the results of the characters multiplied by their weight within the string.
 * If it is less than two, the digit returned is zero, otherwise the remainder of the division by eleven of the sums of the multiplications is returned.
 *
 * @param {string} document Clean string with only the document numbers.
 *
 * @return {number} The verification code found for the given document string.
 */
function calcVerificationDigit(document: string): number {
	let sum = 0;
	let numberWeight = 10;

	for (const char of document) {
		sum += Number(char) * numberWeight;
		numberWeight--;
	}

	return sum % 11 < 2 ? 0 : 11 - (sum % 11);
}

/**
 * Function to check if the fiscalId is a valid number.
 *
 * Check that the string provided is a valid document number by calculating and comparing the last two validation digits.
 *
 * @param {string} formatedFiscalId Dirt string that contains formatted document number.
 *
 * @return {boolean} If the function's input has been validated, true if valid, false if not.
 * @return {null} if the input value is not sufficient to validate: an empty string or less than eleven characters.
 */
export function isValidCPF(formatedFiscalId: string): boolean | null {
	const fiscalId = formatedFiscalId.replace(/[^\w\s]/gi, '');

	if (!fiscalId || fiscalId.length < 11) {
		return null;
	}

	const withoutRepeatedChars = removeRepeatedChars(fiscalId);

	if (withoutRepeatedChars.length == 0) {
		return false;
	}

	const verificationDigits = fiscalId.slice(-2);
	const firstDigit = calcVerificationDigit(fiscalId.slice(0, 9));

	if (firstDigit !== Number(verificationDigits[0])) {
		return false;
	}

	const secondDigit = calcVerificationDigit(fiscalId.slice(1, 10));

	if (secondDigit !== Number(verificationDigits[1])) {
		return false;
	}

	return true;
}

/**
 * Validator that that returns if the current AbstractControl value is a valid fiscalId.
 *
 * @return {boolean} If the function's input has been validated, true if valid, a object "{ cpfNotValid: true }" if not.
 * @return {null} In case that the input value is not sufficient to validate: an empty string or less than eleven characters.
 */
export function CPFValidator() {
	return (control: AbstractControl): Validators => {
		const validationResult = isValidCPF(control.value);

		if (validationResult) {
			return true;
		}

		if (validationResult === false) {
			return { cpfNotValid: true };
		}

		return null;
	};
}
